import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage, useTranslation } from '@/store/languageStore';
import { dashboardService, AvailableWidget } from '@/services/dashboard/dashboardService';
import {
  XMarkIcon,
  TruckIcon,
  MapIcon,
  ChartBarIcon,
  ClockIcon,
  BellIcon,
  CheckCircleIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  BoltSlashIcon,
  Battery0Icon,
  CurrencyDollarIcon,
  ChartPieIcon,
  BanknotesIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';

interface WidgetSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectWidget: (widgetType: string, column: number) => void;
  existingWidgetTypes: string[];
  hiddenTypes?: string[];
  /** Kort som bara finns i designförhandsvisningen och därför saknas i katalogen. */
  extraWidgets?: AvailableWidget[];
  /** Rutnätsläget väljer bredd på kortet, inte en av tre kolumner. */
  chooseColumn?: boolean;
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  truck: TruckIcon,
  map: MapIcon,
  'chart-bar': ChartBarIcon,
  clock: ClockIcon,
  bell: BellIcon,
  'clipboard-check': ClipboardDocumentCheckIcon,
  'document-text': DocumentTextIcon,
  plug: BoltSlashIcon,
  'battery-low': Battery0Icon,
  wrench: WrenchScrewdriverIcon,
  currency: CurrencyDollarIcon,
  'chart-pie': ChartPieIcon,
  banknotes: BanknotesIcon,
  activity: SignalIcon,
};

const WidgetSelector: React.FC<WidgetSelectorProps> = ({
  isOpen,
  onClose,
  onSelectWidget,
  existingWidgetTypes,
  hiddenTypes = [],
  extraWidgets = [],
  chooseColumn = true,
}) => {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const [widgets, setWidgets] = useState<AvailableWidget[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedColumn, setSelectedColumn] = useState(0);

  useEffect(() => {
    if (isOpen) {
      loadWidgets();
      setSelectedColumn(0);
    }
  }, [isOpen]);

  const loadWidgets = async () => {
    try {
      setLoading(true);
      const data = await dashboardService.getAvailableWidgets();
      setWidgets(data);
    } catch (error) {
      console.error('Error loading widgets:', error);
    } finally {
      setLoading(false);
    }
  };

  const { availableWidgets, onDashboardWidgets } = useMemo(() => {
    const known = new Set(widgets.map((widget) => widget.type));
    const visible = [...widgets, ...extraWidgets.filter((widget) => !known.has(widget.type))]
      .filter((w) => !hiddenTypes.includes(w.type));
    const onDashboard = visible.filter((w) => existingWidgetTypes.includes(w.type));
    const available = visible.filter((w) => !existingWidgetTypes.includes(w.type));
    return { availableWidgets: available, onDashboardWidgets: onDashboard };
  }, [widgets, extraWidgets, existingWidgetTypes, hiddenTypes]);

  const handleSelectWidget = (widget: AvailableWidget) => {
    if (!widget.implemented) return;
    if (existingWidgetTypes.includes(widget.type)) return;
    onSelectWidget(widget.type, selectedColumn);
    onClose();
  };

  if (!isOpen) return null;

  const columnNames = [t('widgets.columns.left'), t('widgets.columns.center'), t('widgets.columns.right')];

  const renderCard = (widget: AvailableWidget, mode: 'available' | 'added') => {
    const IconComponent = iconMap[widget.icon] || ChartBarIcon;
    const isAdded = mode === 'added';
    const isImplemented = widget.implemented;

    return (
      <div
        key={`${mode}-${widget.type}`}
        className={`p-4 rounded-xl border-2 transition-all ${
          isAdded
            ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10 cursor-default'
            : !isImplemented
              ? 'border-border opacity-50 cursor-not-allowed'
              : 'border-borderDefault hover:border-blue-500 hover:shadow-lg cursor-pointer'
        }`}
        onClick={() => !isAdded && handleSelectWidget(widget)}
      >
        <div className="flex items-start gap-4">
          <div
            className={`p-3 rounded-xl ${
              isAdded ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
            }`}
          >
            <IconComponent
              className={`w-6 h-6 ${
                isAdded ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'
              }`}
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-textDefault">
                {language === 'sv' ? widget.name : widget.nameEn}
              </h3>
              {isImplemented && !isAdded && (
                <CheckCircleIcon className="w-4 h-4 text-green-500" title={t('widgets.available')} />
              )}
            </div>
            <p className="text-sm text-textSecondary mt-1">
              {language === 'sv' ? widget.description : widget.descriptionEn}
            </p>
            {isAdded && (
              <p className="mt-2 text-xs text-green-700 dark:text-green-300">
                {t(chooseColumn ? 'widgets.alreadyAddedHint' : 'widgets.alreadyAddedOnPage')}
              </p>
            )}
            {!isImplemented && (
              <span className="inline-flex items-center gap-1 mt-2 text-xs text-amber-600 bg-amber-100 dark:bg-amber-900/30 px-2 py-1 rounded">
                <WrenchScrewdriverIcon className="w-3 h-3" />
                {t('widgets.comingSoon')}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-bgElevated rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-borderDefault">
          <h2 className="text-xl font-semibold text-textDefault">{t('widgets.addWidget')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-bgHover text-textTertiary hover:text-textDefault transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {chooseColumn && <div className="px-6 py-4 border-b border-borderDefault bg-bgDefault/50">
          <label className="block text-sm font-medium text-textSecondary mb-2">
            {t('widgets.selectColumn')}
          </label>
          <div className="flex gap-2">
            {columnNames.map((name, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setSelectedColumn(idx)}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  selectedColumn === idx
                    ? 'bg-blue-600 text-white'
                    : 'bg-bgElevated text-textSecondary hover:bg-bgHover'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>}

        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : (
            <div className="space-y-6">
              {onDashboardWidgets.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-textSecondary mb-3">
                    {t('widgets.sectionOnDashboard')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {onDashboardWidgets.map((w) => renderCard(w, 'added'))}
                  </div>
                </section>
              )}

              <section>
                <h3 className="text-sm font-semibold text-textSecondary mb-3">
                  {t('widgets.sectionAvailable')}
                </h3>
                {availableWidgets.length === 0 ? (
                  <p className="text-sm text-textTertiary py-4 text-center">
                    {onDashboardWidgets.length > 0
                      ? t('widgets.alreadyAddedHint')
                      : t('widgets.notInPackage')}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {availableWidgets.map((w) => renderCard(w, 'available'))}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetSelector;
